import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, RefreshCw, ExternalLink } from 'lucide-react';

/**
 * WebhookHealthCard
 *
 * Live observability of Shopify webhook auto-capture for the marketplace.
 * Polls /api/admin/webhooks/diagnostics?topic=orders/create on mount.
 *
 * Status pill semantics:
 *   - GREEN  : at least one webhook arrived AND all of them materialised into orders.
 *   - AMBER  : webhooks arriving but materialisation gap > 0 (some failed processing).
 *   - RED    : webhooks arriving but ZERO orders created (silent swallow). Critical.
 *   - GRAY   : no webhooks logged yet (probably no orders received yet).
 */
export default function WebhookHealthCard() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall('/api/admin/webhooks/diagnostics?topic=orders/create&limit=10');
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to fetch webhook diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading && !data) {
    return (
      <Card data-testid="webhook-health-card-loading">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">Loading webhook health…</div>
          <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="webhook-health-card-error">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-red-700">Webhook Health</div>
            <div className="text-xs text-gray-500 mt-1">Couldn't fetch diagnostics: {error}</div>
          </div>
          <Button size="sm" variant="outline" onClick={fetchData}>
            <RefreshCw className="h-3 w-3 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const m = data?.materialisation || {};
  const logsCount = m.webhook_logs_count || 0;
  const ordersCount = m.orders_via_webhook_source || 0;
  const totalShopifyOrders = m.orders_with_shopify_id_total || 0;
  const noCreds = (data?.recent_no_credentials || []).length;
  const failures = (data?.recent_processing_failures || []).length;

  let pill = { label: 'No data', tone: 'gray', icon: null };
  if (logsCount === 0) {
    pill = { label: 'No webhooks yet', tone: 'gray', icon: null };
  } else if (ordersCount === 0 && logsCount > 0) {
    pill = { label: 'Critical — 0% capture', tone: 'red', icon: AlertTriangle };
  } else if (totalShopifyOrders < logsCount) {
    pill = { label: 'Degraded — gaps', tone: 'amber', icon: AlertTriangle };
  } else {
    pill = { label: 'Healthy', tone: 'green', icon: CheckCircle2 };
  }

  const toneClass = {
    red: 'bg-red-100 text-red-800 border-red-300',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    green: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    gray: 'bg-gray-100 text-gray-700 border-gray-300',
  }[pill.tone];

  const Icon = pill.icon;

  return (
    <Card data-testid="webhook-health-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-gray-800">Shopify Webhook Health</h3>
              <Badge variant="outline" className={`text-xs ${toneClass}`} data-testid="webhook-health-pill">
                {Icon && <Icon className="h-3 w-3 mr-1" />}
                {pill.label}
              </Badge>
            </div>
            <p className="text-xs text-gray-600">
              <span className="font-mono font-semibold">{ordersCount}</span> of{' '}
              <span className="font-mono font-semibold">{logsCount}</span> orders/create webhooks auto-captured into orders
              {totalShopifyOrders > ordersCount && (
                <> · {totalShopifyOrders - ordersCount} via manual sync</>
              )}
            </p>
            {(noCreds > 0 || failures > 0) && (
              <p className="text-xs text-amber-700 mt-1">
                {noCreds > 0 && <>⚠ {noCreds} webhook{noCreds !== 1 ? 's' : ''} rejected (shop_url not registered)</>}
                {noCreds > 0 && failures > 0 && ' · '}
                {failures > 0 && <>⚠ {failures} processing failure{failures !== 1 ? 's' : ''}</>}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              data-testid="webhook-health-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/webhook-manager')}
              data-testid="webhook-health-manage-btn"
            >
              <ExternalLink className="h-3 w-3 mr-1" /> Manage
            </Button>
          </div>
        </div>

        {/* Drilldown: most-recent failures */}
        {pill.tone !== 'green' && pill.tone !== 'gray' && (data?.recent_no_credentials?.length > 0 || data?.recent_processing_failures?.length > 0) && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-blue-700 hover:underline">View recent failures</summary>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {(data?.recent_no_credentials || []).slice(0, 5).map((row, idx) => (
                <div key={`nc-${idx}`} className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="font-semibold text-amber-800">Unrecognised shop</div>
                  <div className="text-gray-700">
                    Sent <span className="font-mono">{row.shop_url}</span> →
                    expected <span className="font-mono">{row.normalized_shop_url}</span>
                  </div>
                  <div className="text-gray-500">Order: {row.shopify_order_number || row.shopify_order_id}</div>
                </div>
              ))}
              {(data?.recent_processing_failures || []).slice(0, 5).map((row, idx) => (
                <div key={`pf-${idx}`} className="bg-red-50 border border-red-200 rounded p-2">
                  <div className="font-semibold text-red-800">Processing failed</div>
                  <div className="text-gray-700 font-mono text-[10px] break-all">{row.error}</div>
                  <div className="text-gray-500">Order: {row.shopify_order_number || row.shopify_order_id}</div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
